/**
 * Question World Module - MCAInput Component
 * Multiple Choice Assertion (True/False per option) input handler
 */

import { Col, Image, Row, Typography } from 'antd';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import toastr from 'toastr';

import { MathInput } from '../common';

import {
    setAnsweredQuestionCount,
    setNoOfSubmittedResponse,
    setOfflineSavedAnswers
} from '../../../../../../stateManager/reducers/studentAuthSlice';

import { shouldUseKatex, radioLabels, bucket_url, MCA_OPTIONS } from '../../helpers';
import { Answer } from '../../helpers/Answer';

const { Text } = Typography;

let answer = null;

const MCAInput = (props) => {
    const {
        isResult,
        question,
        examId,
        groupId,
        examMeta,
        isPracticeQuiz = false,
        onAnswerSelect,
        showAnswer = false
    } = props;
    const dispatch = useDispatch();
    const options = isResult ? question?.questionId?.options : question?.options;
    const optionType = isResult
        ? question?.questionId?.optionType &&
            typeof question?.questionId?.optionType === 'string'
            ? JSON.parse(question?.questionId?.optionType)
            : undefined
        : question?.optionType && typeof question?.optionType === 'string'
            ? JSON.parse(question?.optionType)
            : undefined;

    const initialSelectedOptions = React.useMemo(
        () => (isResult ? [] : Array(question?.options?.length || 0).fill('N')),
        [isResult, question?.options?.length]
    );

    const [selectedOption, setSelectedOption] = useState(initialSelectedOptions || []);
    const [isSubmittingAnswer] = useState(false);

    useEffect(() => {
        const localData = localStorage.getItem('localSavedAnswers');
        if (localData) {
            const localSavedAnswers = JSON.parse(localData);
            if (localSavedAnswers[examId]) {
                Object.keys(localSavedAnswers[examId]).forEach((qid) => {
                    const q = localSavedAnswers[examId][qid];
                    if (qid) {
                        dispatch(setAnsweredQuestionCount({ id: qid, flag: true }));
                    }
                    if (q.offline === true) {
                        dispatch(setOfflineSavedAnswers(qid));
                    }
                });
                if (localSavedAnswers[examId]?.[question._id]) {
                    const ans = localSavedAnswers[examId]?.[question._id]?.answer?.answer;
                    setSelectedOption(ans);
                }
            }
        }
    }, [question, dispatch, examId]);

    const saveToLocalStorage = ({ offline, answer: ans, timestamp }) => {
        if (offline) dispatch(setOfflineSavedAnswers(question._id));
        const getLocalData = localStorage.getItem('localSavedAnswers');
        let isPrevious = false;
        if (getLocalData) {
            const localSavedAnswers = JSON.parse(getLocalData);
            isPrevious = !!localSavedAnswers[examId];
        }
        if (isPrevious) {
            const localSavedAnswers = JSON.parse(getLocalData);
            localSavedAnswers[examId][question._id] = {
                answer: { text: [ans], answer: ans },
                offline,
                timestamp,
                type: 'MCA'
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        } else {
            const localSavedAnswers = {
                [examId]: {
                    [question._id]: {
                        answer: { text: [ans], answer: ans },
                        offline,
                        timestamp
                    }
                }
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        }
    };

    const saveResponseToLocalStorage = ({ count }) => {
        const getLocalData = localStorage.getItem('localSavedAnswers');
        let isPrevious = false;
        if (getLocalData) {
            const localSavedAnswers = JSON.parse(getLocalData);
            isPrevious = !!localSavedAnswers[examId];
        }

        if (isPrevious) {
            const localSavedAnswers = JSON.parse(getLocalData);
            localSavedAnswers[examId][question._id].count = count;
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        } else {
            const localSavedAnswers = {
                [examId]: {
                    [question._id]: {
                        ...[question._id],
                        count
                    }
                }
            };
            localStorage.setItem('localSavedAnswers', JSON.stringify(localSavedAnswers));
        }
    };

    const handleAnswerSubmit = async ({ index, value, timestamp }) => {
        saveToLocalStorage({ answer: value, offline: false, timestamp });

        if (isPracticeQuiz || examMeta?.isInstantPractice) {
            if (onAnswerSelect) {
                onAnswerSelect({ index, value, timestamp });
            }
            return;
        }

        const data = {
            questionId: question._id,
            answer: value,
            timestamp,
            answerIndex: index
        };

        try {
            if (answer) {
                answer.cancel();
            }
            answer = new Answer(
                dispatch,
                groupId,
                examId,
                data,
                question,
                false,
                () => setSelectedOption(initialSelectedOptions),
                !examMeta?.examId?.isPracticeExam
            );
            const response = await answer.handleSubmit();
            if (response?.response?.status === '200') {
                let c = 0;
                value.forEach((v) => {
                    if (v !== 'N') {
                        c++;
                    }
                });
                saveResponseToLocalStorage({ count: c });
                dispatch(
                    setNoOfSubmittedResponse({
                        id: question._id,
                        count: c
                    })
                );
            }
        } catch (err) {
            console.log('ANSWER => answer submit err: ', err);
            saveToLocalStorage({ answer: value, offline: true, timestamp });
        }
    };

    const onOptionSelect = (index, value) => {
        const currentOptions = selectedOption || initialSelectedOptions;
        if (
            isSubmittingAnswer ||
            (MCA_OPTIONS.includes(currentOptions[index]) &&
                examMeta?.multipleTimesSubmission === false)
        ) {
            toastr.error('You cannot change the answer');
            return;
        }

        const option = [...currentOptions];
        option[index] = value;

        setSelectedOption(option);
        deboucedSubmit({ index, value: option, timestamp: Date.now() });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const deboucedSubmit = useCallback(
        debounce(({ index, value, timestamp }) => {
            handleAnswerSubmit({ index, value, timestamp });
        }, 250),
        []
    );

    const answerVerification = (index, value) => {
        const ans = question?.questionId?.answer[index];
        const answerGiven = question?.answer?.[index] || 'N';
        if (answerGiven === 'N') {
            if (value === 'true' && ans === 'true') {
                return ' active ';
            }
            if (value === 'false' && ans === 'false') {
                return ' active ';
            }
            return '';
        }
        if (value === 'true' && ans === 'true') {
            return ' active success-text';
        }
        if (value === 'false' && ans === 'true' && answerGiven === 'false') {
            return ' active-red error-text';
        }
        if (value === 'false' && ans === 'false') {
            return ' active success-text';
        }
        if (value === 'true' && ans === 'false' && answerGiven === 'true') {
            return ' active-red error-text';
        }
        return '';
    };

    const getCorrectAnswerClass = (index) => {
        const ans = question?.questionId?.answer?.[index];
        const answerGiven =
            question?.answer?.[index] || selectedOption?.[index] || 'N';
        if (answerGiven === 'N' && !showAnswer) {
            return '';
        }
        if (showAnswer && ans) {
            if (ans === 'true') {
                return ' success-text ';
            } else if (ans === 'false') {
                return ' success-text ';
            }
        }
        if (answerGiven === 'N') {
            return '';
        }
        if (answerGiven === ans) {
            return ' success-text ';
        }
        return ' error-text ';
    };

    void getCorrectAnswerClass;

    if (!isResult) {
        return (
            <Col xs={23} md={23} offset={1}>
                {options.map((option, index) => {
                    const correctAns = question?.questionId?.answer?.[index];
                    const userSelected = selectedOption?.[index] || 'N';
                    const shouldHighlightCorrect = showAnswer && correctAns;

                    return (
                        <Row key={`option-${index}`} className={' mb-2 flex-nowrap'}>
                            <Col>
                                <div className={' custom-radio mb-0 mr-2'} disabled={true}>
                                    <div className="custom-radio-prepend">
                                        <span
                                            className={
                                                'custom-radio-icon mr-2' +
                                                ('true' === userSelected ? ' active' : ' ') +
                                                (shouldHighlightCorrect && correctAns === 'true'
                                                    ? ' success-text'
                                                    : '') +
                                                (shouldHighlightCorrect &&
                                                    correctAns !== 'true' &&
                                                    userSelected === 'true'
                                                    ? ' error-text'
                                                    : '')
                                            }
                                            onClick={() => {
                                                onOptionSelect(index, 'true');
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            T
                                        </span>
                                        <span
                                            className={
                                                'custom-radio-icon' +
                                                ('false' === userSelected ? ' active' : ' ') +
                                                (shouldHighlightCorrect && correctAns === 'false'
                                                    ? ' success-text'
                                                    : '') +
                                                (shouldHighlightCorrect &&
                                                    correctAns !== 'false' &&
                                                    userSelected === 'false'
                                                    ? ' error-text'
                                                    : '')
                                            }
                                            onClick={() => {
                                                onOptionSelect(index, 'false');
                                            }}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            F
                                        </span>
                                    </div>
                                </div>
                            </Col>
                            <Col>
                                <div className="mr-2">
                                    <span style={{}}>
                                        <strong>{`${radioLabels[index]}.`}</strong>
                                    </span>
                                </div>
                            </Col>
                            <Col className="mr-4">
                                <div className={'d-flex align-center'} disabled={true}>
                                    <span>
                                        {optionType &&
                                            optionType[option] &&
                                            optionType[option] === 'image' ? (
                                            <Image
                                                placeholder
                                                src={bucket_url + option}
                                                width={'100%'}
                                                style={{ objectFit: 'contain' }}
                                            />
                                        ) : (
                                            <Text>
                                                {shouldUseKatex(option) >= 0 ? (
                                                    <MathInput mathItem={option} />
                                                ) : (
                                                    option
                                                )}
                                            </Text>
                                        )}
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    );
                })}
            </Col>
        );
    }

    return (
        <Col xs={23} md={23} offset={1}>
            {options.map((option, index) => {
                return (
                    <Row key={`option-${index}`} className={'mb-2 flex-nowrap'}>
                        <Col>
                            <div
                                className={' custom-radio align-center mb- mr-2'}
                                disabled={true}
                            >
                                <div className="custom-radio-prepend">
                                    <span
                                        className={
                                            'custom-radio-icon ' + answerVerification(index, 'true')
                                        }
                                        onClick={() => {
                                            onOptionSelect(index, 'true');
                                        }}
                                        style={{ marginRight: '10px' }}
                                    >
                                        <span className="child">T</span>
                                    </span>
                                    <span
                                        className={
                                            'custom-radio-icon ' + answerVerification(index, 'false')
                                        }
                                        onClick={() => {
                                            onOptionSelect(index, 'false');
                                        }}
                                    >
                                        <span className="child">F</span>
                                    </span>
                                </div>
                            </div>
                        </Col>
                        <Col>
                            <div className="mr-2">
                                <span style={{}}>
                                    <strong>{`${radioLabels[index]}.`}</strong>
                                </span>
                            </div>
                        </Col>
                        <Col className="mr-4">
                            <div className={'d-flex align-center'} disabled={true}>
                                <span>
                                    {optionType &&
                                        optionType[option] &&
                                        optionType[option] === 'image' ? (
                                        <Image
                                            placeholder
                                            src={bucket_url + option}
                                            width={'100%'}
                                            style={{ objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <Text>
                                            {shouldUseKatex(option) >= 0 ? (
                                                <MathInput mathItem={option} />
                                            ) : (
                                                option
                                            )}
                                        </Text>
                                    )}
                                </span>
                            </div>
                        </Col>
                    </Row>
                );
            })}
        </Col>
    );
};

export default MCAInput;

